import React, { useEffect, useState } from 'react';
import {
	Box,
	Typography,
	TextField,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Paper,
	Grid2,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ImageIcon from '@mui/icons-material/Image';
import { REACT_APP_API_IMG } from '../../../../../utlis';

export interface BasicDetailsCardProps {
	openModal: boolean;
	CloseModal: () => void;
	Vendorme?: VendorType;
	handleSubmit: any;
}

export interface VendorType {
	ownerName?: string;
	mobile?: string;
	email?: string;
	companyAddress?: CompanyAddress;
	logo?: string;
}

export interface CompanyAddress {
	address?: string;
	city?: string;
	state?: string;
	country?: string;
	zipCode?: string;
}

export interface FormValues {
	profilePhoto: File | null;
	address: string;
	city: string;
	state: string;
	country: string;
	zipCode: string;
}

const BasicDetailsModal: React.FC<BasicDetailsCardProps> = ({
	openModal,
	CloseModal,
	Vendorme,
	handleSubmit,
}) => {
	const [preview, setPreview] = useState<string | null>(null);

	const validationSchema = Yup.object({
		profilePhoto: Yup.mixed().required('Profile photo is required'),
		address: Yup.string().required('Address is required'),
		city: Yup.string().required('City is required'),
		state: Yup.string().required('State is required'),
		country: Yup.string().required('Country is required'),
		zipCode: Yup.string().required('Zip code is required'),
	});

	const handelallSubmit = async (values: FormValues, { setSubmitting, resetForm }: any) => {
		const res = await handleSubmit(values, { setSubmitting, resetForm });

		if (res.success) {
			setPreview(null);
		} else {
			setPreview(null);
		}
	};

	useEffect(() => {
		setPreview(Vendorme?.logo || null);
	}, [Vendorme?.logo]);

	return (
		<>
			<Dialog open={openModal} onClose={CloseModal} fullWidth maxWidth="sm">
				<DialogTitle>
					<Typography variant="h6" fontWeight="bold">
						Basic Details
					</Typography>
				</DialogTitle>
				<Formik
					initialValues={{
						profilePhoto: null,
						address: Vendorme?.companyAddress?.address || '',
						city: Vendorme?.companyAddress?.city || '',
						state: Vendorme?.companyAddress?.state || '',
						country: Vendorme?.companyAddress?.country || '',
						zipCode: Vendorme?.companyAddress?.zipCode || '',
					}}
					validationSchema={validationSchema}
					onSubmit={handelallSubmit}
				>
					{({ values, errors, touched, setFieldValue, handleBlur, isSubmitting }) => (
						<Form>
							<DialogContent dividers>
								<Box
									component={Paper}
									p={2}
									mb={2}
									variant="outlined"
									borderRadius={2}
								>
									<Typography variant="subtitle1" fontWeight="medium" mb={1}>
										Profile Information
									</Typography>

									<Grid2 container spacing={2}>
										<Grid2 size={{ xs: 12, md: 6 }}>
											<TextField
												label="Name"
												value={Vendorme?.ownerName || ''}
												disabled
												fullWidth
												size="small"
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 6 }}>
											<TextField
												label="Mobile"
												value={Vendorme?.mobile || ''}
												disabled
												fullWidth
												size="small"
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 6 }}>
											<TextField
												label="Email"
												value={Vendorme?.email || ''}
												disabled
												fullWidth
												sx={{ gridColumn: '1 / span 2' }}
												size="small"
											/>
										</Grid2>
									</Grid2>
								</Box>

								<Box component={Paper} p={2} variant="outlined" borderRadius={2}>
									<Typography variant="subtitle1" fontWeight="medium" mb={1}>
										Company Address
									</Typography>
									<Grid2 container spacing={2}>
										<Grid2 size={{ xs: 12, md: 6 }}>
											<TextField
												label="Address"
												name="address"
												value={values.address}
												onChange={(e) =>
													setFieldValue('address', e.target.value)
												}
												onBlur={handleBlur}
												error={touched.address && Boolean(errors.address)}
												helperText={touched.address && errors.address}
												fullWidth
												multiline
												minRows={2}
												sx={{ gridColumn: '1 / span 2' }}
												size="small"
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 6 }}>
											<TextField
												label="City"
												name="city"
												value={values.city}
												onChange={(e) =>
													setFieldValue('city', e.target.value)
												}
												onBlur={handleBlur}
												error={touched.city && Boolean(errors.city)}
												helperText={touched.city && errors.city}
												fullWidth
												size="small"
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 6 }}>
											<TextField
												label="State"
												name="state"
												value={values.state}
												onChange={(e) =>
													setFieldValue('state', e.target.value)
												}
												onBlur={handleBlur}
												error={touched.state && Boolean(errors.state)}
												helperText={touched.state && errors.state}
												fullWidth
												size="small"
											/>
										</Grid2>

										<Grid2 size={{ xs: 12, md: 6 }}>
											<TextField
												label="Country"
												name="country"
												value={values.country}
												onChange={(e) =>
													setFieldValue('country', e.target.value)
												}
												onBlur={handleBlur}
												error={touched.country && Boolean(errors.country)}
												helperText={touched.country && errors.country}
												fullWidth
												size="small"
											/>
										</Grid2>

										<Grid2 size={{ xs: 12, md: 6 }}>
											<TextField
												label="Zip Code"
												name="zipCode"
												value={values.zipCode}
												onChange={(e) =>
													setFieldValue('zipCode', e.target.value)
												}
												onBlur={handleBlur}
												error={touched.zipCode && Boolean(errors.zipCode)}
												helperText={touched.zipCode && errors.zipCode}
												fullWidth
												size="small"
											/>
										</Grid2>
									</Grid2>
									{/* <Button
										variant="outlined"
										startIcon={<ImageIcon sx={{ color: 'red' }} />}
										component="label"
										fullWidth
										sx={{
											gridColumn: '1 / span 2',
											mt: 1,
											border: '2px dashed gray',
											padding: 2,
											color: '#747474',
										}}
									>
										Upload New Profile
										<input
											type="file"
											accept="image/*"
											hidden
											onChange={(e) => {
												if (
													e.currentTarget.files &&
													e.currentTarget.files[0]
												) {
													setFieldValue(
														'profilePhoto',
														e.currentTarget.files[0]
													);
													setPreview(
														URL.createObjectURL(
															e.currentTarget.files[0]
														)
													);
												}
											}}
										/>
									</Button> */}
									<Button
										variant="outlined"
										startIcon={<ImageIcon sx={{ color: 'red' }} />}
										component="label"
										fullWidth
										sx={{
											gridColumn: '1 / span 2',
											mt: 1,
											border: '2px dashed gray',
											padding: 2,
											color: '#747474',
										}}
									>
										<span>Upload New Profile</span>
										<input
											type="file"
											accept="image/*"
											hidden
											onChange={(e) => {
												if (
													e.currentTarget.files &&
													e.currentTarget.files[0]
												) {
													setFieldValue(
														'profilePhoto',
														e.currentTarget.files[0]
													);
													setPreview(
														URL.createObjectURL(
															e.currentTarget.files[0]
														)
													);
												}
											}}
										/>
									</Button>

									{preview && (
										<Box
											sx={{
												gridColumn: '1 / span 2',
												mt: 2,
												display: 'flex',
												justifyContent: 'start',
												mb: 2,
											}}
										>
											<img
												src={`${REACT_APP_API_IMG}${preview}`}
												alt="Profile Preview"
												style={{
													maxHeight: 40,
													borderRadius: 8,
													boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
												}}
											/>
										</Box>
									)}

									{touched.profilePhoto && errors.profilePhoto && (
										<Typography color="error">{errors.profilePhoto}</Typography>
									)}
								</Box>
							</DialogContent>

							<DialogActions sx={{ px: 3, pb: 2 }}>
								<Button onClick={CloseModal} color="secondary" variant="outlined">
									Cancel
								</Button>
								<Button type="submit" variant="contained" disabled={isSubmitting}>
									Save
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</>
	);
};

export default BasicDetailsModal;
